import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";

const PROJECTS = [
  {
    label: "Festival · 2024",
    title: "Rumpelkammer",
    blurb: "Eine der zwei Stages der Medimeisterschaften — 360° Sound, rotierende Spiegelfläche, grandMA 3.",
    img: "/events/rumpelkammer-cover.jpg",
    accent: "#F49800",
    details: {
      description:
        "Die Rumpelkammer. Eine der zwei Stages, die das Hochebene-Kollektiv bei den Medimeisterschaften auf die Beine stellt. 2024 erschufen wir diese Bühne — mit ganz viel Liebe, komplett aus Holz und mit 360° Sound von d&b Audiotechnik. Dabei waren sechs V10P und vier SL-GSUB. Die Beleuchtung wurde schlicht gehalten: Robe Tetra2, SGM Q7 sowie Blinder und Rundumbeleuchtung der Zäune. Das Highlight war die große rotierende Spiegelfläche, die zusätzlich von vier Movingheads auf dem Dach angeleuchtet wurde — angesteuert über eine grandMA 3 Light. Es war ein Fest.",
      tags: ["Bühnenaufbau", "d&b Audiotechnik", "grandMA 3", "Robe Tetra2", "SGM Q7", "360° Sound"],
      gallery: [
        "/events/rumpelkammer-cover.jpg",
        "/events/rumpelkammer-2.jpg",
        "/events/rumpelkammer-3.jpg",
        "/events/rumpelkammer-4.jpg",
        "/events/rumpelkammer-5.jpg",
      ],
    },
  },
  {
    label: "Campus-Festival · 2024",
    title: "Hochschule Ansbach",
    blurb: "Anhängerbühne mit vier bestückten Truss, grandMA 3 Light, Dynacord Sound — zehn 18\" Hörner für Wumms.",
    img: "/events/ansbach-cover.jpg",
    accent: "#E71681",
    details: {
      description:
        "Die Hochschule Ansbach unterstützen wir immer wieder bei allerlei Veranstaltungen. Die Fachschaft stellt mehrmals pro Jahr verschiedene Events auf die Beine — Mensa-Partys und einmal jährlich das große Campus-Festival. Beim Campus-Festival 2024 bauten wir auf einer Anhängerbühne, mit vier bestückten Truss im Dach und einer fünften abgehängten Backtruss. Angesteuert wurde über eine grandMA 3 Light. Beim Sound setzen wir auf ein Dynacord System — untenrum sorgten zehn 18\" Hörner für genügend Wumms.",
      tags: ["Campus-Festival", "Anhängerbühne", "grandMA 3", "Dynacord", "Truss", "Lichttechnik"],
      gallery: [
        "/events/ansbach-cover.jpg",
        "/events/ansbach-2.jpg",
        "/events/ansbach-3.jpg",
        "/events/ansbach-4.jpg",
      ],
    },
  },
  {
    label: "Festival · 2025",
    title: "Medimeisterschaften",
    blurb: "",
    img: "/events/medimeisterschaften-cover.jpg",
    accent: "#F49800",
    details: {
      description: "Auch 2025 wieder dabei — mit komplett neuer Bühne, wieder vollständig aus Holz. Beim Sound kein Kompromiss: Funktion-One Power für feine Musik, so klar und warm wie es sein soll. Optische Akzente kamen vom Zoom-Effekt der Litecraft AT60, Blindern und PAR-Kannen. Als Highlight: Laserbars über die gesamte Breite der Booth. Nebel und Haze durften nicht fehlen — angesteuert über die grandMA3 light.",
      tags: ["Bühnenbau", "Funktion-One", "grandMA3", "Litecraft AT60", "Laserbars", "Haze & Nebel"],
      gallery: [
        "/events/medimeisterschaften-cover.jpg",
        "/events/medimeisterschaften-2.jpg",
        "/events/medimeisterschaften-3.jpg",
        "/events/medimeisterschaften-4.jpg",
        "/events/medimeisterschaften-5.jpg",
      ],
    },
  },
  {
    label: "Lichtinstallation · 2024",
    title: "Weiher Feier",
    blurb: "Uplighting, Laser und Moving-Heads — der zauberhafte Weiher im perfekten Licht.",
    img: "/events/weiher-cover.jpg",
    accent: "#6B1F6B",
    details: {
      description:
        "Für die Weiher Feier 2024 durften wir den zauberhaften Weiher ins richtige Licht rücken. Rundherum Uplighting mit LED-Scheinwerfern sorgte für eine warme, einladende Atmosphäre. Laser und Moving-Heads projizierten beeindruckende Lichtspiele auf die Bäume gegenüber — Natur und Technik im perfekten Einklang.",
      tags: ["Uplighting", "LED", "Laser", "Moving-Heads", "Outdoor", "Atmosphäre"],
      gallery: [
        "/events/weiher-cover.jpg",
        "/events/weiher-2.jpg",
        "/events/weiher-3.jpg",
        "/events/weiher-4.jpg",
      ],
    },
  },
];

// N panels, each 100vw wide → container = N*100vw
// To see all panels: translate by -(N-1)*100vw = -((N-1)/N)*100% of container
const TRAVEL = `-${(((PROJECTS.length - 1) / PROJECTS.length) * 100).toFixed(4)}%`;

export const Projects = () => {
  const wrapRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });

  const x = useTransform(scrollYProgress, [0, 1], ["0%", TRAVEL]);

  return (
    <>
      <section
        id="projects"
        data-testid="projects-section"
        data-glow="projects"
        ref={wrapRef}
        className="relative"
        style={{ height: `${PROJECTS.length * 100}vh` }}
      >
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col">
          <div className="px-6 sm:px-10 lg:px-20 pt-28 pb-6 flex items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-3">02 — Referenzen</p>
              <h2 className="h2-display">Showreel.</h2>
            </div>
            <p className="hidden md:block font-mono text-[11px] uppercase tracking-[0.25em] text-mute">
              scroll → horizontal
            </p>
          </div>

          <motion.div
            className="flex h-full will-change-transform"
            style={{ x, width: `${PROJECTS.length * 100}vw` }}
          >
            {PROJECTS.map((p, idx) => (
              <ProjectCard
                key={p.title}
                project={p}
                index={idx}
                total={PROJECTS.length}
                onOpen={() => setActiveProject(p)}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {ReactDOM.createPortal(
        <AnimatePresence>
          {activeProject && (
            <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

const ProjectCard = ({ project, index, total, onOpen }) => (
  <article
    className="relative shrink-0 h-full flex items-end px-6 sm:px-10 lg:px-20 pb-16 lg:pb-24"
    style={{ width: "100vw" }}
    data-testid={`project-${index}`}
  >
    <div className="relative w-full h-[68vh] rounded-[24px] overflow-hidden">
      <img
        src={project.img}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "brightness(0.82) saturate(1.1)" }}
        loading="lazy"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 30%, rgba(5,5,6,0.05) 55%, rgba(5,5,6,0.88) 100%)",
        }}
      />
      <div
        className="absolute inset-0 mix-blend-overlay"
        style={{
          background: `radial-gradient(circle at 20% 90%, ${project.accent}40, transparent 60%)`,
        }}
      />

      <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12 flex items-end justify-between gap-6">
        <div className="max-w-2xl">
          <p
            className="font-mono text-[11px] uppercase tracking-[0.25em] mb-3"
            style={{ color: project.accent }}
          >
            {project.label}
          </p>
          <h3 className="font-display text-3xl lg:text-5xl font-medium tracking-[-0.02em] leading-[1.05]">
            {project.title}
          </h3>
        </div>
        <motion.button
          onClick={onOpen}
          className="flex shrink-0 w-12 h-12 rounded-full items-center justify-center cursor-pointer"
          style={{
            background: "rgba(255,255,255,0.08)",
            border: `1px solid ${project.accent}66`,
            backdropFilter: "blur(8px)",
          }}
          whileHover={{ scale: 1.1, background: `${project.accent}22` }}
          whileTap={{ scale: 0.96 }}
          aria-label={`${project.title} Details ansehen`}
        >
          <ArrowUpRight size={18} className="text-offwhite" />
        </motion.button>
      </div>

      <div className="absolute top-6 left-6 lg:top-8 lg:left-10">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mute">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </div>
    </div>
  </article>
);

const ProjectModal = ({ project, onClose }) => {
  const [photoIdx, setPhotoIdx] = useState(0);
  const gallery = project.details.gallery;

  const prev = (e) => { e.stopPropagation(); setPhotoIdx((i) => (i - 1 + gallery.length) % gallery.length); };
  const next = (e) => { e.stopPropagation(); setPhotoIdx((i) => (i + 1) % gallery.length); };

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(5,5,6,0.88)", backdropFilter: "blur(12px)" }}
      />

      <motion.div
        className="relative w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto rounded-t-[28px] sm:rounded-[28px]"
        style={{ background: "rgba(14,14,18,0.98)", border: "1px solid rgba(244,152,0,0.12)" }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }}
        >
          <X size={16} className="text-offwhite" />
        </button>

        {/* Photo carousel */}
        <div className="relative w-full aspect-video rounded-t-[28px] overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            <motion.img
              key={photoIdx}
              src={gallery[photoIdx]}
              alt={`${project.title} ${photoIdx + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </AnimatePresence>

          {gallery.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ background: "rgba(5,5,6,0.65)", backdropFilter: "blur(8px)" }}
              >
                <ChevronLeft size={18} className="text-offwhite" />
              </button>
              <button
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ background: "rgba(5,5,6,0.65)", backdropFilter: "blur(8px)" }}
              >
                <ChevronRight size={18} className="text-offwhite" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setPhotoIdx(i); }}
                    className="rounded-full transition-all duration-200"
                    style={{
                      width: i === photoIdx ? 20 : 6,
                      height: 6,
                      background: i === photoIdx ? project.accent : "rgba(255,255,255,0.3)",
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="p-7 sm:p-10">
          <p
            className="font-mono text-[11px] uppercase tracking-[0.25em] mb-3"
            style={{ color: project.accent }}
          >
            {project.label}
          </p>
          <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-[-0.02em] mb-4">
            {project.title}
          </h3>
          <p className="body-mute text-base leading-relaxed mb-7">
            {project.details.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {project.details.tags.map((tag) => (
              <span
                key={tag}
                className="font-mono text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full"
                style={{
                  background: `${project.accent}12`,
                  border: `1px solid ${project.accent}33`,
                  color: `${project.accent}cc`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
